import { lazy, Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import type { RouteRecord } from 'vite-react-ssg';
import { Layout } from '@/components/layout/Layout';
import { ScrollToTop } from '@/components/layout/ScrollToTop';
import App from '@/App';

// Pages we statically prerender — imported eagerly so vite-react-ssg's SSR
// renderer sees them synchronously. Lazy() routes don't pick up the Helmet
// context during SSG, so they would ship with index.html-fallback meta only.
import Index from '@/pages/Index';
import Losningar from '@/pages/Losningar';
import TransportPage from '@/pages/solutions/TransportPage';
import ServicePage from '@/pages/solutions/ServicePage';
import EcommercePage from '@/pages/solutions/EcommercePage';
import Tjanster from '@/pages/Produkter';
import AtlasPage from '@/pages/products/AtlasPage';
import ValidPage from '@/pages/products/ValidPage';
import AdresstwattPage from '@/pages/products/AdresstwattPage';
import Nyheter from '@/pages/Nyheter';
import Villkor from '@/pages/Villkor';
import Developer from '@/pages/Developer';
import OmGeposit from '@/pages/OmGeposit';
import Kontakt from '@/pages/Kontakt';
import Integritetspolicy from '@/pages/Integritetspolicy';
import Bestall from '@/pages/Bestall';
import NotFound from '@/pages/NotFound';

// Lazy-loaded: not prerendered, only ever shown via client-side routing.
function lazyPage(loader: () => Promise<{ default: React.ComponentType }>) {
  return async () => {
    const m = await loader();
    return { Component: m.default };
  };
}

function PageSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
      {children}
    </Suspense>
  );
}

const AdminLogin = lazy(() => import('@/pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('@/components/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const ProtectedRoute = lazy(() => import('@/components/admin/ProtectedRoute').then(m => ({ default: m.ProtectedRoute })));

function AdminShell() {
  return (
    <PageSuspense>
      <ProtectedRoute>
        <AdminLayout />
      </ProtectedRoute>
    </PageSuspense>
  );
}

const routes: RouteRecord[] = [
  {
    path: '/',
    element: (
      <App>
        <ScrollToTop />
        <Layout>
          <PageSuspense>
            <Outlet />
          </PageSuspense>
        </Layout>
      </App>
    ),
    children: [
      { index: true, element: <Navigate to="/sv" replace /> },

      // Public language-prefixed routes (eager, prerendered for sv + en)
      { path: ':lang', element: <Index /> },
      { path: ':lang/losningar', element: <Losningar /> },
      { path: ':lang/losningar/transport', element: <TransportPage /> },
      { path: ':lang/losningar/service', element: <ServicePage /> },
      { path: ':lang/losningar/ehandel', element: <EcommercePage /> },
      { path: ':lang/tjanster', element: <Tjanster /> },
      { path: ':lang/tjanster/atlas', element: <AtlasPage /> },
      { path: ':lang/tjanster/valid', element: <ValidPage /> },
      { path: ':lang/tjanster/adresstwatt', element: <AdresstwattPage /> },
      { path: ':lang/information/nyheter', element: <Nyheter /> },
      { path: ':lang/information/villkor', element: <Villkor /> },
      { path: ':lang/developer', element: <Developer /> },
      { path: ':lang/om-geposit', element: <OmGeposit /> },
      { path: ':lang/kontakt', element: <Kontakt /> },
      { path: ':lang/integritetspolicy', element: <Integritetspolicy /> },
      { path: ':lang/bestall', element: <Bestall /> },

      // Lazy: dynamic / transactional / no-prerender
      { path: ':lang/information/:slug', lazy: lazyPage(() => import('@/pages/NewsArticle')) },
      { path: ':lang/uppsagning', lazy: lazyPage(() => import('@/pages/Uppsagning')) },
      { path: ':lang/uppsagning/bekraftelse', lazy: lazyPage(() => import('@/pages/UpsagningBekraftelse')) },

      // Admin (noindex per robots.txt)
      { path: ':lang/admin/login', lazy: lazyPage(() => import('@/pages/admin/AdminLogin')) },
      {
        path: ':lang/admin',
        element: <AdminShell />,
        children: [
          { index: true, lazy: lazyPage(() => import('@/pages/admin/AdminDashboard')) },
          { path: 'orders', lazy: lazyPage(() => import('@/pages/admin/AdminOrders')) },
          { path: 'pricing', lazy: lazyPage(() => import('@/pages/admin/AdminPricing')) },
          { path: 'news', lazy: lazyPage(() => import('@/pages/admin/AdminNews')) },
          { path: 'news/:id', lazy: lazyPage(() => import('@/pages/admin/AdminNewsEditor')) },
          { path: 'documents', lazy: lazyPage(() => import('@/pages/admin/AdminDocuments')) },
          { path: 'announcements', lazy: lazyPage(() => import('@/pages/admin/AdminAnnouncements')) },
          { path: 'users', lazy: lazyPage(() => import('@/pages/admin/AdminUsers')) },
          { path: 'settings', lazy: lazyPage(() => import('@/pages/admin/AdminSettings')) },
          { path: 'cancellations', lazy: lazyPage(() => import('@/pages/admin/AdminCancellations')) },
          { path: 'cancellations/stats', lazy: lazyPage(() => import('@/pages/admin/AdminCancellationStats')) },
          { path: 'cancellations/settings', lazy: lazyPage(() => import('@/pages/admin/AdminCancellationSettings')) },
        ],
      },

      // Legacy SEO redirects — kept as belt-and-suspenders for users who
      // bypass the load balancer. The real 301s now happen in the GCP URL
      // map (see update-gcp-config.sh).
      { path: 'tjänster/atlas-karta-med-postnummer-för-zonindelning/*', element: <Navigate to="/sv/tjanster/atlas" replace /> },
      { path: 'tjänster/valid-adressvalidering-och-autocomplete/*', element: <Navigate to="/sv/tjanster/valid" replace /> },
      { path: 'tjänster/routing-körvägar-rutter-och-räckviddsområden/*', element: <Navigate to="/sv/tjanster/atlas" replace /> },
      { path: 'tjänster/*', element: <Navigate to="/sv/tjanster" replace /> },
      { path: 'tjänster', element: <Navigate to="/sv/tjanster" replace /> },
      { path: 'developer/*', element: <Navigate to="/sv/developer" replace /> },
      { path: 'developer', element: <Navigate to="/sv/developer" replace /> },
      { path: 'om-oss/*', element: <Navigate to="/sv/om-geposit" replace /> },
      { path: 'om-oss', element: <Navigate to="/sv/om-geposit" replace /> },
      { path: 'om-geposit', element: <Navigate to="/sv/om-geposit" replace /> },
      { path: 'nyheter/*', element: <Navigate to="/sv/information/nyheter" replace /> },
      { path: 'nyheter', element: <Navigate to="/sv/information/nyheter" replace /> },
      { path: 'kontakt', element: <Navigate to="/sv/kontakt" replace /> },
      { path: 'integritetspolicy', element: <Navigate to="/sv/integritetspolicy" replace /> },
      { path: 'bestall', element: <Navigate to="/sv/bestall" replace /> },
      { path: 'losningar/*', element: <Navigate to="/sv/losningar" replace /> },
      { path: 'tjanster/*', element: <Navigate to="/sv/tjanster" replace /> },
      { path: 'information/*', element: <Navigate to="/sv/information/nyheter" replace /> },
      { path: 'en/services/atlas-zoning/*', element: <Navigate to="/en/tjanster/atlas" replace /> },
      { path: 'en/services/valid-address-check-and-verification/*', element: <Navigate to="/en/tjanster/valid" replace /> },
      { path: 'en/services/*', element: <Navigate to="/en/tjanster" replace /> },
      { path: 'en/about-us/*', element: <Navigate to="/en/om-geposit" replace /> },
      { path: 'en/contact/*', element: <Navigate to="/en/kontakt" replace /> },
      { path: 'admin/login', element: <Navigate to="/sv/admin/login" replace /> },
      { path: 'admin/*', element: <Navigate to="/sv/admin" replace /> },
      { path: 'admin', element: <Navigate to="/sv/admin" replace /> },
      { path: 'uppsagning', element: <Navigate to="/sv/uppsagning" replace /> },
      { path: 'uppsagning/bekraftelse', element: <Navigate to="/sv/uppsagning/bekraftelse" replace /> },

      { path: '*', element: <NotFound /> },
    ],
  },
];

export default routes;
export { PRERENDERED_PATHS } from './prerendered-paths';
